import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { PersonaRecibe, PersonaRecibeResumen } from './persona-recibe';

export interface PersonalRecibeState {
  list: PaginatedList<PersonaRecibeResumen>;
  current: PersonaRecibe;
  actives: PersonaRecibeResumen[];

  nuevo: () => void;
  cargarPaginadas: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivas: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarPersonaRecibe) => Promise<number | null>;
  actualizar: (id: number, request: ActualizarPersonaRecibe) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarPersonaRecibe {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

export interface ActualizarPersonaRecibe {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

const { showSuccessToast } = useUIStore.getState();

export const usePersonalRecibeStore = create<PersonalRecibeState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as PersonaRecibe,
  actives: [],

  nuevo: () =>
    set({
      current: {} as PersonaRecibe
    }),
  cargarPaginadas: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<PersonaRecibeResumen>>(
      `/personal-recibe/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivas: async () => {
    const data = await makeGet<Array<PersonaRecibeResumen>>('/personal-recibe/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<PersonaRecibe>(`/personal-recibe/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarPersonaRecibe) => {
    const data = await makePost<RegistrarPersonaRecibe, number | null>(
      '/personal-recibe/',
      request
    );

    if (data.isFailure) return null;

    showSuccessToast('Registro agregado');

    set({
      current: {} as PersonaRecibe
    });

    return data.result;
  },
  actualizar: async (id: number, request: ActualizarPersonaRecibe) => {
    const data = await makePatch<ActualizarPersonaRecibe, PersonaRecibe>(
      `/personal-recibe/${id}`,
      request
    );

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/personal-recibe/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activa: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/personal-recibe/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activa: false
      }
    });

    return true;
  }
}));
